<template>
    <!-- Loading State - Sadece loading varken göster -->
    <div v-if="loading" class="loading-overlay">
        <LoadingSpinner />
    </div>
    
    <!-- Form Content - Loading yokken göster -->
    <div v-else class="form-wrapper">
        <div v-for="logo in getFormLogo()" :key="logo._id" class="logo-container">
            <img
                :src="`https://static.supsis.live/${logo.path + logo._id}.${logo.ext}`"
                :alt="logo.name"
                :style="{ width: logo.width + 'px', height: logo.height + 'px'}"
            />
        </div>
        <div :class="['container', { 'special-background': isSpecial }]">
            <div v-if="error">{{ error }}</div>
            <div v-if="sortedFields.length && !submissionSuccess">
                <component
                    v-for="field in sortedFields"
                    :key="field._id"
                    :is="getComponent(field.type)"
                    v-bind="getProps(field)"
                    v-model="formValues[field._id]"
                    :showError="showError"
                    :formId="formId"
                    :order="field.order"
                    v-on="field.type === 'send' ? { submit: submitForm } : {}"
                    @validate="handleValidation"
                />
            </div>
            <div v-if="submissionSuccess">
                <SubmitForm />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Title from "./item/Title.vue";
import FullName from "./item/FullName.vue";
import Email from "./item/Email.vue";
import Address from "./item/Address.vue";
import PhoneNumber from "./item/PhoneNumber.vue";
import DatePicker from "./item/DatePicker.vue";
import ShortText from "./item/ShortText.vue";
import LongText from "./item/LongText.vue";
import Number from "./item/Number.vue";
import Hour from "./item/Hour.vue";
import Spinner from "./item/Spinner.vue";
import SendBtn from "./item/SendBtn.vue";
import StarRating from "./item/StarRating.vue";
import RatingScale from "./item/RatingScale.vue";
import SeparationElement from "./item/SeparationElement.vue";
import SubmitForm from "./SubmitForm.vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import FileUpload from "./item/FileUpload.vue";
import DropDownList from "./item/DropDownList.vue";
import SingleChoice from "./item/SingleChoice.vue";
import MultipleSelection from "./item/MultipleSelection.vue";
import FormImage from "./item/FormImage.vue";

function generateRandomId() {
    return "xxxxxxx".replace(/x/g, function () {
        const r = (Math.random() * 16) | 0;
        return r.toString(16);
    });
}

export default {
    name: "FormContainer",
    components: {
        Title,
        FullName,
        Email,
        Address,
        PhoneNumber,
        DatePicker,
        ShortText,
        LongText,
        Number,
        Hour,
        Spinner,
        SendBtn,
        StarRating,
        RatingScale,
        SeparationElement,
        SubmitForm,
        LoadingSpinner,
        FileUpload,
        DropDownList,
        SingleChoice,
        MultipleSelection,
        FormImage,
    },
    data() {
        return {
            formId: this.$route.params.formId,
            siteId: this.$route.params.siteId,
            formValues: {},
            userDeviceId: null,
            hasSendButton: false,
            validationErrors: {},
            showError: false,
            queryUsername: null,
            queryFullName: null,
            isSpecial: false,
            submissionTriggered: false,
        };
    },
    created() {
        const queryString = this.$route.fullPath.split("?")[1];
        const queryParams = new URLSearchParams(queryString);
        this.queryUsername = queryParams.get("username");
        this.queryFullName = queryParams.get("fullName");

        let userDeviceId = this.getCookie("userDeviceId");
        if (!userDeviceId) {
            userDeviceId = generateRandomId();
            this.setCookie("userDeviceId", userDeviceId, 365);
        }
        this.userDeviceId = userDeviceId;
        this.fetchFormData({ siteId: this.siteId, formId: this.formId }).then((res) => {
            if (!res) {
                this.$router.push({ name: "NotFound" });
            }
        });
    },
    computed: {
        ...mapGetters("form", ["formData", "loading", "error", "submissionSuccess"]),
        sortedFields() {
            if (this.formData && this.formData.formFields) {
                this.checkForSendButton(this.formData.formFields);
                return [...this.formData.formFields].sort((a, b) => a.order - b.order);
            }
            return [];
        },
    },
    watch: {
        formData: {
            handler(newFormData) {
                if (newFormData && newFormData.formFields) {
                    newFormData.formFields.forEach((field) => {
                        if (field.type === 'fileUpload') {
                            this.$set(this.formValues, field._id, {
                                ...this.formValues[field._id],
                                fieldId: field._id,
                                values: this.formValues[field._id]?.values || []
                            });
                        } else if (field.type === 'multipleSelection') {
                            this.$set(this.formValues, field._id, field.value || []);
                        } else {
                            this.$set(this.formValues, field._id, field.value || "");
                        }
                    });
                }
            },
            immediate: true,
            deep: true,
        },
        loading(isLoading) {
            // Loading varken body scroll'unu kilitle
            if (isLoading) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    },
    methods: {
        ...mapActions("form", ["fetchFormData", "submitFormData"]),
        getFormLogo() {
            return this.formData?.form?.logo || [];
        },
        getComponent(type) {
            switch (type) {
                case "title":
                    return "Title";
                case "fullName":
                    return "FullName";
                case "email":
                    return "Email";
                case "address":
                    return "Address";
                case "phone":
                    return "PhoneNumber";
                case "datePicker":
                    return "DatePicker";
                case "shortText":
                    return "ShortText";
                case "longText":
                    return "LongText";
                case "number":
                    return "Number";
                case "hour":
                    return "Hour";
                case "spinner":
                    return "Spinner";
                case "send":
                    return "SendBtn";
                case "starRating":
                    return "StarRating";
                case "ratingScale":
                    return "RatingScale";
                case "separationElement":
                    return "SeparationElement";
                case "fileUpload":
                    return "FileUpload";
                case "dropDownList":
                    return "DropDownList";
                case "singleChoice":
                    return "SingleChoice";
                case "multipleSelection":
                    return "MultipleSelection";
                case "image":
                    return "FormImage";
                default:
                    return null;
            }
        },
        getProps(field) {
            switch (field.type) {
                case "title":
                    return { ...field.title };
                case "fullName":
                    return { ...field.fullName };
                case "email":
                    return { ...field.email };
                case "address":
                    return { ...field.address };
                case "phone":
                    return { ...field.phone };
                case "datePicker":
                    return { ...field.datePicker };
                case "shortText":
                    return { ...field.shortText };
                case "longText":
                    return { ...field.longText };
                case "number":
                    return { ...field.number };
                case "hour":
                    return { ...field.hour };
                case "spinner":
                    return { ...field.spinner };
                case "send":
                    return { ...field.send };
                case "starRating":
                    return { ...field.starRating };
                case "ratingScale":
                    return { ...field.ratingScale };
                case "separationElement":
                    return { ...field.separationElement };
                case "fileUpload":
                    return { 
                        ...field.fileUpload, 
                        siteId: this.siteId,
                        fieldId: field._id
                    };
                case "dropDownList":
                    return { ...field.dropDownList, siteId: this.siteId };
                case "singleChoice":
                    return { ...field.singleChoice, siteId: this.siteId };
                case "multipleSelection":
                    return {
                        ...field.multipleSelection,
                        siteId: this.siteId,
                        value: Array.isArray(this.formValues[field._id])
                            ? this.formValues[field._id]
                            : this.formValues[field._id]
                                ? [this.formValues[field._id]]
                                : []
                    };
                case "image":
                    return { ...field.image , siteId: this.siteId };
                default:
                    return {};
            }
        },
        getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(";").shift();
        },
        setCookie(name, value, days) {
            const d = new Date();
            d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
            const expires = `expires=${d.toUTCString()}`;
            document.cookie = `${name}=${value};${expires};path=/`;
        },
        checkForSendButton(fields) {
            this.hasSendButton = fields.some((field) => field.type === "send");
        },
        handleValidation({ fieldId, hasError }) {
            this.$set(this.validationErrors, fieldId, hasError);
        },
        async submitForm(eventData) {
            // SendBtn kontrolü - sadece send butonu varsa ve tıklandığında gönder
            if (!this.hasSendButton) {
                console.log('Form gönderimi iptal edildi: Send butonu bulunamadı');
                return;
            }

            // Explicit submit kontrolü - sadece SendBtn'den gelen submit'leri kabul et
            if (!eventData || !eventData.triggeredByButton) {
                console.log('Form gönderimi iptal edildi: Submit butonu dışında tetiklendi');
                return;
            }

            const hasErrors = Object.values(this.validationErrors).some((error) => error);
            this.showError = hasErrors;
            if (hasErrors) {
                return;
            }

            const isSpecial = this.$route.query.special === "true";
            if (isSpecial) {
                const lastSubmitTime = localStorage.getItem("lastSubmitTime");
                const currentTime = Date.now();
                const waitTime = 300000;

                if (lastSubmitTime) {
                    const timeElapsed = currentTime - lastSubmitTime;
                    if (timeElapsed < waitTime) {
                        const remainingTime = Math.ceil((waitTime - timeElapsed) / 1000 / 60);
                        alert(`Lütfen ${remainingTime} dakika bekleyin.`);
                        return;
                    }
                }

                localStorage.setItem("userDeviceId", this.userDeviceId);
            }

            const formData = {
                visitorId: this.formValues.visitorId || "",
                formId: this.formId,
                formVersionNumber: 1,
                answers: this.sortedFields.map((field) => {
                    let value = this.formValues[field._id];
                    if (typeof value === "object" && value !== null) {
                        if (field.type === "fullName") {
                            value = [value.firstName, value.lastName];
                        } else if (field.type === "address") {
                            value = [value.addressLine1, value.addressLine2, value.city, value.state, value.postalCode];
                        } else if (field.type === "phone") {
                            value = [value.phoneNumber];
                        } else if (field.type === "datePicker") {
                            value = value.date;
                        } else if (field.type === "shortText") {
                            value = value.shortText;
                        } else if (field.type === "longText") {
                            value = value.longText;
                        } else if (field.type === "dropDownList") {
                            value = value.selected;
                        } else if (field.type === "singleChoice") {
                            value = value.selected;
                        } else if (field.type === "multipleSelection") {
                            value = Array.isArray(value) ? value.filter((file) => file !== "") : value ? [value] : [];
                        } else if (field.type === "number") {
                            value = value.number;
                        } else if (field.type === "hour") {
                            value = [value.time, value.ampm];
                        } else if (field.type === "spinner") {
                            value = value.spinnerValue;
                        } else if (field.type === "starRating") {
                            value = value.rating;
                        } else if (field.type === "ratingScale") {
                            value = value.rating;
                        } else if (field.type === "fileUpload") {
                            value = value.values;
                        } else if (field.type === "email") {
                            value = value.email;
                        } else if (!Array.isArray(value)) {
                            value = Object.values(value);
                        }
                    } else {
                        value = [value];
                    }
                    return {
                        formFieldId: field._id,
                        value: value,
                    };
                }),
                userDeviceId: this.userDeviceId,
            };

            if (this.queryUsername && this.queryFullName) {
                formData.answers.push({
                    formFieldId: "66bf592e53ccfaf47176212f",
                    value: {
                        username: this.queryUsername,
                        fullName: this.queryFullName,
                    },
                    userDeviceId: this.userDeviceId,
                });
            }

            await this.submitFormData({ siteId: this.siteId, formData });

            if (isSpecial) {
                localStorage.setItem("lastSubmitTime", Date.now().toString());
            }

            Object.keys(localStorage).forEach((key) => {
                if (key !== "userDeviceId") {
                    localStorage.removeItem(key);
                }
            });
        }
    },
    mounted() {
        const special = this.$route.query.special;
        if (special === "true") {
            this.isSpecial = true;
            const div = document.getElementById("container");
            div.style.background = "red";
        }
    },
    beforeDestroy() {
        // Component destroy olurken body scroll'unu geri aç
        document.body.style.overflow = '';
    }
};
</script>

<style lang="scss" scoped>
/* Loading Overlay - Tam ekran loading */
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    z-index: 999999;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(248, 250, 252, 0.98);
    backdrop-filter: blur(10px);
}

.form-wrapper {
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    padding: 0;
    margin: 0;
}

.logo-container {
    display: inline-block;
    text-align: center;
    width: 100%;
    margin-bottom: var(--space-4);
    margin-top: 10px;
    
    img {
        max-width: 100%;
        height: auto;
        object-fit: contain;
    }
}

.container {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-xl);
    box-shadow: 
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04),
        0 0 0 1px rgba(255, 255, 255, 0.1);
    padding: clamp(1.5rem, 4vw, 3rem);
    width: 100%;
    max-width: min(90vw, 750px);
    margin: clamp(1rem, 3vw, 2rem) auto;
    position: relative;
    transition: all var(--transition);
    box-sizing: border-box;
    
    /* Modern glassmorphism effect */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.05) 100%
        );
        border-radius: inherit;
        pointer-events: none;
        z-index: 1;
    }
    
    /* Content above overlay */
    & > * {
        position: relative;
        z-index: 2;
    }
    
    /* Hover effect for better interactivity */
    &:hover {
        transform: translateY(-2px);
        box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.15),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }
    
    /* Focus-within for accessibility */
    &:focus-within {
        box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.15),
            0 0 0 3px rgba(99, 102, 241, 0.1),
            0 0 0 1px var(--primary);
    }
}

.special-background {
    background: transparent !important;
    backdrop-filter: none !important;
    border: none !important;
    box-shadow: none !important;
    
    &::before {
        display: none !important;
    }
    
    &:hover {
        background: transparent !important;
        transform: none !important;
        box-shadow: none !important;
    }
    
    &:focus-within {
        box-shadow: none !important;
        border: none !important;
    }
}

/* Modern responsive design */
@media (max-width: 768px) {
    .container {
        max-width: min(95vw, 100vw - 2rem);
        margin: clamp(0.75rem, 2vw, 1.5rem) auto;
        padding: clamp(1rem, 3vw, 2rem);
        border-radius: var(--radius-lg);
        box-shadow: 
            0 10px 25px -5px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(255, 255, 255, 0.1);
        
        /* Reduce transform on mobile for performance */
        &:hover {
            transform: translateY(-1px);
        }
    }
}

@media (max-width: 480px) {
    .container {
        max-width: calc(100vw - 1rem);
        margin: 0.5rem auto;
        padding: clamp(0.75rem, 2vw, 1.5rem);
        border-radius: var(--radius-md);
        box-shadow: 
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        
        /* Disable hover effects on very small screens */
        &:hover {
            transform: none;
        }
    }
    
    .special-background {
        border-radius: 0 !important;
        border: none !important;
    }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
    .container {
        transition: none;
        
        &:hover {
            transform: none;
        }
        
        &::before {
            animation: none;
        }
    }
}

/* Print styles */
@media print {
    .loading-overlay {
        display: none !important;
    }
    
    .container {
        background: white !important;
        box-shadow: none !important;
        border: 1px solid #ccc !important;
        backdrop-filter: none !important;
        
        &::before {
            display: none !important;
        }
    }
}
</style>